import { EventEmitter, h, Component, Prop, State, Host, Element, Event } from '@stencil/core';

import { hasSlotContent, getCSSVariable } from '../../../services/utils';

@Component({
  tag: 'pk-simple-header',
  styleUrl: './scss/pk-simple-header.scss',
  shadow: true,
})
export class PkSimpleHeader {
  // Own Properties
  // ====================

  private containerElem: HTMLDivElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkSimpleHeaderElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================
  @State() hasLogo: boolean;
  @State() hasSponsor: boolean;
  @State() hasAction: boolean;
  @State() hasTitlePrefix: boolean;

  // Public Property API
  // ========================

  /** Set a token as the background color */
  @Prop({ reflect: true }) bgColor: string;

  /** Set the background image */
  @Prop({ reflect: true }) bgImg: string;

  /** Set if the page with this header is closable*/
  @Prop({ reflect: true }) disableClose?: boolean = false;

  /** The href of the close button. */
  @Prop({ reflect: true }) closeHref?: string;

  /** Set the height of the bar (default = 60px) */
  @Prop({ reflect: true }) height: number = 60;

  /** Set the icon color */
  @Prop({ reflect: true }) iconColor: string = 'text-02';

  /** Set the size of the icon (default = icon size 24) */
  @Prop({ reflect: true }) iconSize: number;

  /** Set the title text */
  @Prop({ reflect: true }) text: string;

  /** Set the title heading */
  @Prop({ reflect: true }) titleHeading: 'h1' | 'h2' | 'h3' = 'h3';

  /** Set a token as the title color */
  @Prop({ reflect: true }) titleColor: string;

  /** Set the width of the bar (default = 100%) */
  @Prop({ reflect: true }) width: string = '100%';

  /** Set the max-width of the bar content (default = 100%) */
  @Prop({ reflect: true }) contentMaxWidth: string = '100%';

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Handler to be called when the close button is clicked */
  @Event({ bubbles: false }) pkClose: EventEmitter<UIEvent>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleOnClick = (ev?: UIEvent) => this.pkClose.emit(ev);

  private handleLogoDivider = () => {
    this.hasLogo = hasSlotContent(this.containerElem, 'competition-logo');
  };

  private handleSponsorDivider = () => {
    this.hasSponsor = hasSlotContent(this.containerElem, 'sponsor');
  };

  private handleActionSlot = () => {
    this.hasAction = hasSlotContent(this.containerElem, 'action');
  };

  private handleTitlePrefix = () => {
    this.hasTitlePrefix = hasSlotContent(this.containerElem, 'title-prefix');
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.bgColor && { '--simple-header--bg-color': getCSSVariable(this.bgColor) }),
      ...(this.bgImg && { '--simple-header--bg-img': `url(${this.bgImg})` }),
      ...(this.titleColor && {
        '--simple-header--title-color': getCSSVariable(this.titleColor),
      }),
      ...(this.height && { '--simple-header--height': `${this.height}px` }),
      ...(this.width && { '--simple-header--width': `${this.width}` }),
      ...(this.contentMaxWidth && { '--simple-header--content-max-width': `${this.contentMaxWidth}` }),
    };

    const Heading = this.titleHeading;

    return (
      <Host style={{ ...style }}>
        <div
          class={{
            wrapper: true,
            'with-sponsor': !!this.hasSponsor,
          }}
        >
          <div
            class="flex w-full max-w-[--simple-header--content-max-width] items-stretch"
            part="content"
            ref={(el: HTMLDivElement) => (this.containerElem = el)}
          >
            <div class="flex flex-[0]" part="logo-container">
              <slot name="competition-logo" onSlotchange={this.handleLogoDivider} />
            </div>
            {this.hasLogo && !!this.text && <pk-divider class="mx-s my-0" direction="vertical" part="divider" />}
            <div
              class={{ 'title-container': true, flex: !this.hasTitlePrefix, 'flex-col': this.hasTitlePrefix }}
              part="title-container"
            >
              <span class={{ hidden: !this.hasTitlePrefix }}>
                <slot name="title-prefix" onSlotchange={this.handleTitlePrefix} />
              </span>
              {this.text?.trim() && (
                <Heading class="title" part="title">
                  {this.text}
                </Heading>
              )}
            </div>
            <div class={{ hidden: !this.hasAction, 'mr-m': this.hasAction, 'flex flex-[0] items-center': true }}>
              <slot name="action" onSlotchange={this.handleActionSlot} />
            </div>
            <div
              class="sponsor-container flex flex-col items-center justify-center sm:flex-row"
              part="sponsor-container"
            >
              <span class="text-xs3 text-text-01 sm:mr-xs">
                <slot name="sponsor-label" />
              </span>
              <slot name="sponsor" onSlotchange={this.handleSponsorDivider} />
            </div>
            {!this.disableClose && (
              <div class="close-container ml-auto flex items-center" part="close-container">
                {this.hasSponsor && <pk-divider class="mx-s my-0" direction="vertical" part="divider" />}
                <pk-button
                  appearance="icon"
                  class="close-btn"
                  onClick={this.handleOnClick}
                  linkHref={this.closeHref}
                  part="close-btn"
                  soft
                >
                  <pk-icon
                    name="navigation-close"
                    color={this.iconColor}
                    size={this.iconSize}
                    role="img"
                    title="close-icon"
                    part="navigation-icon"
                  />
                  <span class="sr-only">Close</span>
                </pk-button>
              </div>
            )}
          </div>
        </div>
      </Host>
    );
  }
}
